import { Alert, Button, Dialog, HStack, VStack } from '@chakra-ui/react'
import type { ReactNode } from 'react'

type Props = {
  title: string
  /** Heading of the red in-dialog alert. Ignored when there is no error. */
  errorTitle: string
  /** Body of the red in-dialog alert: a `502`, a `503`, or a fallback. */
  errorDescription?: string
  /**
   * The `409` state. When present it **replaces** the error alert rather than
   * stacking under it: a conflict is not a failure, and rendering both would
   * tell the operator two different things about the same response.
   */
  conflictPanel?: ReactNode
  cancelLabel: string
  /**
   * Submit button rendered by the caller — each form decides its own palette,
   * label and disabled rule, and the conflict retry lives on the panel.
   */
  submitButton: ReactNode
  children: ReactNode
}

/**
 * Dialog chrome for the three Salesforce link forms.
 *
 * A local twin of `LicenseFormDialog` on purpose, not an import of it: that
 * one renders its single inline slot as a red `Alert`, which is wrong for a
 * `confirmation_required` — the service is asking the operator to confirm,
 * not reporting a fault. Convergence is APP-2478.
 */
export function SalesforceLinkFormDialog({
  title,
  errorTitle,
  errorDescription,
  conflictPanel,
  cancelLabel,
  submitButton,
  children,
}: Readonly<Props>) {
  return (
    <>
      <Dialog.Header pb={3}>
        <Dialog.Title>{title}</Dialog.Title>
        <Dialog.CloseTrigger />
      </Dialog.Header>
      <Dialog.Body>
        <VStack align="stretch" gap={4}>
          {conflictPanel ??
            (errorDescription ? (
              /* `role` is explicit: Chakra v3's `Alert.Root` renders a plain
                 div, and this alert appears only after a failed submit, so
                 without it the failure is announced to nobody. */
              <Alert.Root status="error" role="alert">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Title>{errorTitle}</Alert.Title>
                  <Alert.Description>{errorDescription}</Alert.Description>
                </Alert.Content>
              </Alert.Root>
            ) : null)}
          {children}
        </VStack>
      </Dialog.Body>
      <Dialog.Footer>
        <HStack gap={2}>
          <Dialog.ActionTrigger asChild>
            <Button variant="outline">{cancelLabel}</Button>
          </Dialog.ActionTrigger>
          {submitButton}
        </HStack>
      </Dialog.Footer>
    </>
  )
}
